import {gql} from '@apollo/client';
import React from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {GridCellSummary, SampleDetails} from 'constants/SampleConstants';
import {readFragmentFromCache} from 'graphql-client/ApolloWrappers';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {getNoteLinkedGroupAdapters} from 'notebook/notes/groups/NoteLinkedGroupAdapters';
import {UnknownNoteLinkedGroup} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import getNotePartType from 'notebook/notes/util/getNotePartType';
import inspectAllNoteParts from 'notebook/notes/util/inspectAllNoteParts';
import {convertRegistrationTableToTable} from 'notebook/registration-tables/RegistrationTableNotePartUtil';
import {AllTagSchemaInterfacesQuery} from 'notebook/sample-propagation/sample-collection/__generated/AllTagSchemaInterfacesQuery';
import {getPlatesFromCell} from 'notebook/sample-propagation/sample-collection/getGridsFromCell';
import getSampleDetailsFromCell from 'notebook/sample-propagation/sample-collection/getSampleDetailsFromCell';
import {
  isTypeInSampleFilters,
  SampleFilter,
  SampleFilters,
  SampleFilterType,
  SimpleSampleFilters,
} from 'notebook/sample-propagation/SampleFilter';
import dedupeSampleDetails from 'notebook/sample-propagation/util/dedupeSampleDetails';
import getAppropriateSampleFilter, {
  inferSampleFilterFromLookupConfig,
} from 'notebook/sample-propagation/util/getAppropriateSampleFilter';
import {getSchemaForColumn} from 'notebook/sample-propagation/util/getSchemaForColumn';
import {
  getSchemaIdForContainersColumn,
  getSchemaIdForEntityColumn,
} from 'notebook/sample-propagation/util/getSchemaIdForColumn';
import {makeWellSampleDetails} from 'notebook/sample-propagation/util/makeSampleDetails';
import {ColumnDataType, guessColumnDataType} from 'notebook/structured-tables/columns/ColumnDataType';
import {
  ColumnDefinition,
  ColumnGroup,
  ContainersColumnDefinition,
  isWithSchemaFieldColumnDefinition,
  SchemaFieldColumnDefinition,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {CustomColumnDataType} from 'notebook/structured-tables/custom-columns/CustomColumnConstants';
import {isStructuredTableMetadata} from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {
  getStructuredTableAdapterFromTable,
  isKnownStructuredTableAdapter,
} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  BaseDedicatedLookupStructuredTableColumn,
  GenericStructuredTableColumn,
  StructuredTableColumn,
  StructuredTableKind,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import convertStructuredTableToTable from 'notebook/structured-tables/util/convertStructuredTableToTable';
import {doesUserHavePermissionToViewTable} from 'notebook/structured-tables/util/doesUserHavePermissionToViewTable';
import notePartToTableDatas, {
  CollectibleLegacyTableType,
  CollectibleTableData,
  CollectibleTableType,
} from 'notebook/structured-tables/util/notePartToTableDatas';
import {Row} from 'notebook/tables/constants/RowConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {Table, TableData, TableViewProps} from 'notebook/tables/constants/TableConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {TransferTableColumnGroup} from 'notebook/transfer-tables/TransferTableConstants';
import {Note} from 'notebook/util/NoteUtilConstants';
import {getStore} from 'redux-stores/Store';
import {isInGridPositionFormat} from 'samples/SampleUtil';
import {FieldType, getRequiredLinkStorableSchema, isSnapshotField} from 'schemas/fields/SchemaFieldConstants';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import APIUtil from 'util/APIUtil';
import * as logging from 'util/Logging';
import removeNulls from 'util/removeNulls';
import {Optional} from 'util/Types';
import {WorkflowSelector} from 'workflows/reduceWorkflows';

// This import has to occur after the above imports to avoid circular dependencies
import InputTablesSamplesSourceCollector from './InputTablesSamplesSourceCollector';

/**
 * This component is used to gather samples to pass to the AddSamples modal.
 */

export interface SamplesByColumn {
  displayName: string;
  // columnDefinition is useful for distinguishing storable columns as their columnType can be non-unique within the table
  columnDefinition?: ColumnDefinition; // Also used for analytics
  column?: StructuredTableColumn; // Only used for analytics
  sampleFilter: SampleFilter;
  samples: Array<SampleDetails>;
}

export interface SamplesSource {
  name: string;
  icon: BenchlingIconName;
  samplesByColumn: Array<SamplesByColumn>;

  table: UnknownStructuredTable | null; // Only used for analytics
}

interface SamplesSourceCollectorProps {
  note: Note | null;
  entryId: string;
  omitTableNames?: Array<string>;
  preFilter?: SampleFilters;
  includeInputTables?: boolean;
  children: (sampleSources: Array<SamplesSource>) => JSX.Element;
}

export const STRUCTURED_TABLE_TYPES_THAT_MIGHT_HAVE_SAMPLES: Array<CollectibleTableType> = [
  StructuredTableKind.REGISTRATION,
  CollectibleLegacyTableType.LEGACY_REGISTRATION_TABLE,
  StructuredTableKind.RUN_OUTPUT_SAMPLES,
  StructuredTableKind.BOX_CREATION,
  StructuredTableKind.PLATE_CREATION,
  StructuredTableKind.RESULTS,
  StructuredTableKind.TRANSFER,
  StructuredTableKind.INVENTORY,
  StructuredTableKind.PROCESS_TASK,
  StructuredTableKind.LOOKUP,
];

export function getFinalizedRows(table: Table): Array<Row> {
  return table.table.filter((_row, index) => {
    // Gathering data for BNCH-12356
    const rowData = table.rowData?.[index];
    if (rowData == null) {
      logging.warn('Incomplete rowData array', {
        extra: {
          table,
          rowIndex: index,
        },
      });
    }
    return rowData?.isFinalized;
  });
}

interface SampleCollectionOptions {
  /** Table names to skip collecting samples for. */
  omitTableNames?: Array<string>;
  /** Only collects samples matching list of filters. If undefined, allows any type. */
  preFilter?: SampleFilters;
  /** Whether to skip bioentity link samples. */
  skipLinkedSamples?: boolean;
  /** Whether to remove duplicate samples. Default is true. */
  removeDuplicates?: boolean;
  /** Whether to also collect samples from unsubmitted rows. Default is false (only finalized).
   * However, when the table is an inventory table or lookup table, we always include unfinalized rows. */
  includeUnfinalizedRows?: boolean;
}

export type GetSampleCollectionOptions = Pick<
  SampleCollectionOptions,
  'skipLinkedSamples' | 'removeDuplicates' | 'includeUnfinalizedRows'
>;

export function getSamplesFromSingleColumn(
  sourceTable: Table,
  columnIndex: number,
  columnType: TableColumnType,
  sampleFilter: SampleFilter | null,
  columnName: string,
  computedTable?: Array<Array<EvaluatedCell>>,
  options: GetSampleCollectionOptions = {}
): SamplesByColumn | null {
  options = {
    ...options,
    removeDuplicates: options.removeDuplicates ?? true,
    includeUnfinalizedRows: options.includeUnfinalizedRows ?? false,
  };

  const isLookupTable =
    isStructuredTableMetadata(sourceTable.metadata) &&
    sourceTable.metadata.kind === StructuredTableKind.LOOKUP;
  const isInventoryTable =
    isStructuredTableMetadata(sourceTable.metadata) &&
    sourceTable.metadata.kind === StructuredTableKind.INVENTORY;
  // Since lookup tables do not have a submit button, we do not enforce finalized row requirement.
  // Inventory tables may be used without submitting, so do not enforce finalized row requirement.
  // Allow getting samples from unfinalized rows when enhanced AFST config flag is enabled.
  const rows =
    isLookupTable || isInventoryTable || options.includeUnfinalizedRows
      ? sourceTable.table
      : getFinalizedRows(sourceTable);

  const samples = rows
    .map((row, index) =>
      getSampleDetailsFromCell(
        // Fall back to sourceTable if computedTable is not provided
        computedTable && computedTable[index] ? computedTable[index][columnIndex] : row[columnIndex],
        columnType,
        options.skipLinkedSamples
      )
    )
    .filter((value): value is Array<SampleDetails> => value != null)
    .flat();
  return samples.length > 0
    ? {
        displayName: columnName,
        // Since sampleFilter is not set for lookup columns or snapshot fields, we take one of the collected samples to
        // determine the appropriate sampleFilter.
        sampleFilter: sampleFilter ?? getAppropriateSampleFilter(samples[0]),
        samples: options.removeDuplicates ? dedupeSampleDetails(samples) : samples,
        columnDefinition: sourceTable.columnConstraints[columnIndex]!.columnDefinition!,
        column: sourceTable.columnConstraints[columnIndex]!.structuredTableColumn!,
      }
    : null;
}

function isLookupColumn<T>(
  column: GenericStructuredTableColumn<T> | undefined
): column is BaseDedicatedLookupStructuredTableColumn<T> {
  return column?.columnType === TableColumnType.DEDICATED_LOOKUP;
}

function doColumnGroupsMatch(
  group1: ColumnGroup | null | undefined,
  group2: ColumnGroup | null | undefined
): boolean {
  return (group1 == null && group2 == null) || group1 === group2;
}

export function findMatchingPositionColumnIndex(
  sourceTable: Table,
  plateColumnGroup: ColumnGroup | null | undefined
): number {
  const allMatchingColumns = sourceTable.columnConstraints.filter((colConstraint) => {
    const colDef = colConstraint?.columnDefinition;
    return (
      colDef != null &&
      colDef.type === TableColumnType.POSITION &&
      doColumnGroupsMatch(colDef.columnGroup, plateColumnGroup)
    );
  });
  // We can only accept single matches
  return allMatchingColumns.length === 1 ? sourceTable.columnConstraints.indexOf(allMatchingColumns[0]) : -1;
}

function getWellSamples(
  sourceTable: Table,
  sampleFilter: SampleFilter,
  plateColumn: ColumnDefinition,
  plateColumnIndex: number,
  computedTable: Array<Array<EvaluatedCell>> | undefined,
  options: GetSampleCollectionOptions
): SamplesByColumn | null {
  // We need to ensure we get the position column which matches the plate column. "Matching" here means that
  // it has the same column group (source, target, or null).
  const {columnGroup} = plateColumn;
  const matchingPositionColumnIndex = findMatchingPositionColumnIndex(sourceTable, columnGroup);
  if (matchingPositionColumnIndex < 0) {
    return null;
  }

  const positionColumn = sourceTable.columnConstraints[matchingPositionColumnIndex]!.columnDefinition!;
  const rows = options.includeUnfinalizedRows ? sourceTable.table : getFinalizedRows(sourceTable);
  const wellSamples = removeNulls(
    rows.map((row, index): SampleDetails | null => {
      // Prefer the computed (evaluated) plate cell when available so that child cells in a
      // merged range pick up the parent's plate value. Single-input columns (e.g. a destination
      // plate shared across many wells) persist the plate value only on the merged-range parent
      // — child cells are cleared — while the evaluator propagates the parent's value into
      // every child cell in the evaluation snapshot. Without this, well rows whose plate column
      // is a merge child see an empty raw cell and are silently dropped.
      const plateCell = computedTable?.[index]?.[plateColumnIndex] ?? row[plateColumnIndex];
      const positionCell = row[matchingPositionColumnIndex];
      const cellError =
        computedTable?.[index]?.[matchingPositionColumnIndex].error ??
        computedTable?.[index]?.[plateColumnIndex].error;

      // When harvesting wells, the plate column must have exactly one plate
      const plates = getPlatesFromCell(plateCell);
      if (plates.length !== 1) {
        return null;
      }

      const typedValue = positionCell.typedValue;
      const isPositionCellFilled =
        (typedValue == null && positionCell.text) ||
        typedValue?.type === BaseTypedValueType.GRID_COORDINATES ||
        typedValue?.type === BaseTypedValueType.STRING;

      if (!isPositionCellFilled) {
        return null;
      }

      let position: GridCellSummary | string;
      if (typedValue?.type === BaseTypedValueType.GRID_COORDINATES) {
        position = typedValue.gridCoordinates;
      } else {
        position = typedValue?.rawValue ?? positionCell.text.trim();
        if (!isInGridPositionFormat(position)) {
          return null;
        }
      }

      const plateInfo = plates[0];
      return makeWellSampleDetails(
        {id: plateInfo.id, name: plateInfo.name, barcode: plateInfo.barcode},
        position,
        cellError
      );
    })
  );
  return wellSamples.length > 0
    ? {
        displayName:
          columnGroup === TransferTableColumnGroup.SOURCE
            ? 'Source well'
            : columnGroup === TransferTableColumnGroup.TARGET
              ? 'Destination well'
              : 'Well',
        sampleFilter,
        samples: options.removeDuplicates ? dedupeSampleDetails(wellSamples) : wellSamples,
        // This actually has two columns, but for analytics purposes, we're treating it as just the position.
        columnDefinition: positionColumn,
        column: sourceTable.columnConstraints[matchingPositionColumnIndex]!.structuredTableColumn!,
      }
    : null;
}

function allowSampleType(sampleFilter: SampleFilterType, allowableTypes?: SampleFilters): boolean {
  return allowableTypes == null || isTypeInSampleFilters(allowableTypes, sampleFilter);
}

// The function is named tentatively because it will include all storable_link columns, and they might
// be filled with containers instead of plates! That's not a problem, as those values are filtered later in
// a call to getPlatesFromCell.
function mightThisColumnHavePlates(columnDefinition?: ColumnDefinition | null): boolean {
  return (
    columnDefinition != null &&
    (columnDefinition.type === TableColumnType.PLATE ||
      columnDefinition.type === TableColumnType.PARENT_STORABLE ||
      (columnDefinition.type === TableColumnType.TAG_VALUE &&
        columnDefinition.tagSchemaFieldType === FieldType.STORABLE_LINK))
  );
}

function getBoxSamples(
  preFilter: Array<SampleFilter> | undefined,
  sourceTable: TableData & TableViewProps,
  colIndex: number,
  columnName: string,
  computedTable: Array<Array<EvaluatedCell>> | undefined,
  options: GetSampleCollectionOptions
): SamplesByColumn | null {
  if (!allowSampleType(SampleFilterType.BOX, preFilter)) {
    return null;
  }

  // Non-plate containers are filtered here
  return getSamplesFromSingleColumn(
    sourceTable,
    colIndex,
    TableColumnType.BOX,
    SimpleSampleFilters.BOX,
    columnName,
    computedTable,
    options
  );
}

function getPlateSamples(
  preFilter: Array<SampleFilter> | undefined,
  sourceTable: TableData & TableViewProps,
  colIndex: number,
  columnName: string,
  computedTable: Array<Array<EvaluatedCell>> | undefined,
  options: GetSampleCollectionOptions
): SamplesByColumn | null {
  if (!allowSampleType(SampleFilterType.PLATE, preFilter)) {
    return null;
  }

  // Non-plate containers are filtered here
  return getSamplesFromSingleColumn(
    sourceTable,
    colIndex,
    TableColumnType.PLATE,
    SimpleSampleFilters.PLATE,
    columnName,
    computedTable,
    options
  );
}

function getContainerSamples(
  preFilter: Array<SampleFilter> | undefined,
  columnDefinition: Optional<ColumnDefinition>,
  sourceTable: TableData & TableViewProps,
  colIndex: number,
  columnName: string,
  computedTable: Array<Array<EvaluatedCell>> | undefined,
  options: GetSampleCollectionOptions
): SamplesByColumn | null {
  if (
    !(
      allowSampleType(SampleFilterType.CONTAINER, preFilter) &&
      columnDefinition != null &&
      (columnDefinition.type === TableColumnType.CONTAINERS ||
        columnDefinition.type === TableColumnType.TAG_VALUE) &&
      ([ColumnDataType.CONTAINER, ColumnDataType.STORABLE] as Array<ColumnDataType | undefined>).includes(
        guessColumnDataType(columnDefinition)?.type
      )
    )
  ) {
    return null;
  }
  const sampleFilter: SampleFilter = {type: SampleFilterType.CONTAINER};
  const schemaIdForColumn = columnDefinition
    ? getSchemaIdForContainersColumn(columnDefinition as ContainersColumnDefinition) ||
      getRequiredLinkStorableSchema((columnDefinition as SchemaFieldColumnDefinition).schemaField)?.id
    : null;
  if (schemaIdForColumn != null) {
    sampleFilter.schemaId = schemaIdForColumn;
  }

  return getSamplesFromSingleColumn(
    sourceTable,
    colIndex,
    TableColumnType.CONTAINERS,
    sampleFilter,
    columnName,
    computedTable,
    options
  );
}

const primitiveFieldTypeToFilter: Partial<Record<FieldType, SampleFilter>> = {
  [FieldType.FLOAT]: {type: SampleFilterType.FLOAT},
  [FieldType.INTEGER]: {type: SampleFilterType.INTEGER},
  [FieldType.TEXT]: {type: SampleFilterType.STRING},
  [FieldType.BOOLEAN]: {type: SampleFilterType.BOOLEAN},
};

const fieldTypeToFilter: Partial<Record<FieldType, SampleFilter>> = {
  ...primitiveFieldTypeToFilter,
  [FieldType.BIOENTITY_LINK]: {type: SampleFilterType.BIOENTITY},
  [FieldType.SAMPLE_LINK]: {type: SampleFilterType.BIOENTITY},
  [FieldType.STORABLE_LINK]: {type: SampleFilterType.STORABLE},
};

const dataTypeToFilter: Record<CustomColumnDataType, SampleFilter | null> = {
  [CustomColumnDataType.FLOAT]: {type: SampleFilterType.FLOAT},
  [CustomColumnDataType.INTEGER]: {type: SampleFilterType.INTEGER},
  [CustomColumnDataType.TEXT]: {type: SampleFilterType.STRING},
  [CustomColumnDataType.BOOLEAN]: {type: SampleFilterType.BOOLEAN},
  // TODO(BNCH-91720): Add a Sample Filter for Dates (and make this non-nullable?)
  [CustomColumnDataType.DATE]: null,
  [CustomColumnDataType.DATETIME]: null,
};

/**
 * EXISTING_BIOENTITY, SAMPLE, TAG_VALUE with BIOENTITY_LINK, SAMPLE_GROUP_SAMPLE with SAMPLE_LINK,
 * and ANTIBODY_DOMAIN columns all contain bioentities.
 */
function isBioentityColumn(
  columnType?: TableColumnType,
  columnDefinition?: ColumnDefinition | null
): boolean {
  return (
    columnType === TableColumnType.EXISTING_BIOENTITY ||
    columnType === TableColumnType.SAMPLE ||
    (columnDefinition?.type === TableColumnType.TAG_VALUE &&
      columnDefinition.tagSchemaFieldType === FieldType.BIOENTITY_LINK) ||
    (columnDefinition?.type === TableColumnType.SAMPLE_GROUP_SAMPLE &&
      columnDefinition.tagSchemaFieldType === FieldType.SAMPLE_LINK) ||
    columnType === TableColumnType.ANTIBODY_DOMAIN
  );
}

/**
 * Returns samples from all columns in a source table for the columns whose type is compatible with `preFilter`.
 */
export function getAllSamplesByColumn(
  sourceTable: Table,
  preFilter?: SampleFilters,
  computedTable?: Array<Array<EvaluatedCell>>,
  options: GetSampleCollectionOptions = {}
): Array<SamplesByColumn> {
  options = {
    ...options,
    removeDuplicates: options.removeDuplicates ?? true,
    includeUnfinalizedRows: options.includeUnfinalizedRows ?? false,
  };
  const samplesByColumn: Array<SamplesByColumn> = [];
  // Get all columns with samples
  for (const [colIndex, columnConstraint] of sourceTable.columnConstraints.entries()) {
    if (columnConstraint == null) {
      continue;
    }
    // While we cache the type here, we also use `columnDefinition?.type` below as needed,
    // to assist TS in narrowing the type of `columnDefinition`
    const columnType: TableColumnType | undefined = columnConstraint.structuredTableColumn?.columnType;
    const columnName =
      sourceTable.columnLabels[colIndex] ?? columnConstraint.structuredTableColumn?.name ?? '(Unknown)';
    const {columnDefinition} = columnConstraint;
    const columnMightHavePlates = mightThisColumnHavePlates(columnDefinition);
    // If this is a plate column, see if we can also harvest WELL samples into a separate SamplesByColumn
    // group. (I.e., the user can propagate plates as a whole, or wells, which are plate+position.)
    // Wells will immediately precede the matching PLATE samples.
    if (
      allowSampleType(SampleFilterType.WELL, preFilter) &&
      columnMightHavePlates &&
      columnDefinition != null
    ) {
      // Non-plate containers are filtered here
      const wellSamples = getWellSamples(
        sourceTable,
        SimpleSampleFilters.WELL,
        columnDefinition,
        colIndex,
        computedTable,
        options
      );
      if (wellSamples != null) {
        samplesByColumn.push(wellSamples);
      }
    }

    // Lookup Column
    if (
      isLookupColumn(columnConstraint.structuredTableColumn) &&
      // Lookup values are computed, so do not bother trying to collect samples if computedTable is unavailable
      computedTable != null
    ) {
      // Since the output type of a lookup column is not readily available, we set sampleFilter to null.
      // After collecting the samples from the column, we will set its proper value.
      let sampleFilter: SampleFilter | null = null;

      if (sourceTable.metadata && 'schemas' in sourceTable.metadata) {
        assert(
          columnConstraint.columnDefinition!.type === TableColumnType.DEDICATED_LOOKUP,
          'Column type should be lookup'
        );
        const lookupConfig = columnConstraint.columnDefinition.lookupConfig;
        const baseSchema = sourceTable.metadata.schemas[0];
        sampleFilter = inferSampleFilterFromLookupConfig(lookupConfig, baseSchema) ?? null;
      }

      if (sampleFilter == null || allowSampleType(sampleFilter.type, preFilter)) {
        const samplesInColumn = getSamplesFromSingleColumn(
          sourceTable,
          colIndex,
          TableColumnType.DEDICATED_LOOKUP,
          sampleFilter,
          columnName,
          computedTable,
          options
        );

        if (samplesInColumn != null) {
          samplesByColumn.push(samplesInColumn);
        }
      }
    }
    // Snapshot fields
    else if (
      columnDefinition != null &&
      isWithSchemaFieldColumnDefinition(columnDefinition) &&
      isSnapshotField(columnDefinition.schemaField)
    ) {
      // Like lookup columns, first try to infer the snapshot field type from its lookupConfig.
      // Otherwise, try to get the type from the column definition.
      // If both fail, set sampleFilter to null and infer its proper value from the first collected sample.
      let sampleFilter: SampleFilter | null = null;
      if (sourceTable.metadata && 'schemas' in sourceTable.metadata) {
        const lookupConfig = columnDefinition.schemaField.lookupConfig;
        const baseSchema = sourceTable.metadata.schemas[0];
        sampleFilter = inferSampleFilterFromLookupConfig(lookupConfig, baseSchema) ?? null;
      }
      if (sampleFilter == null && fieldTypeToFilter.hasOwnProperty(columnDefinition.tagSchemaFieldType)) {
        sampleFilter = {
          ...fieldTypeToFilter[columnDefinition.tagSchemaFieldType]!,
          schemaId: getSchemaForColumn(columnDefinition)?.id ?? undefined,
        };
      }

      if (sampleFilter == null || allowSampleType(sampleFilter.type, preFilter)) {
        const samplesInColumn = getSamplesFromSingleColumn(
          sourceTable,
          colIndex,
          TableColumnType.TAG_VALUE,
          sampleFilter,
          columnName,
          computedTable,
          options
        );

        if (samplesInColumn != null) {
          samplesByColumn.push(samplesInColumn);
        }
      }
    }
    // Bioentity columns (including appropriate tagValue columns)
    else if (
      allowSampleType(SampleFilterType.BIOENTITY, preFilter) &&
      isBioentityColumn(columnType, columnDefinition)
    ) {
      // Bioentity columns (including appropriate tagValue columns)
      // We build a SampleFilter to describe what's in this column of data
      const sampleFilter: SampleFilter = {type: SampleFilterType.BIOENTITY};
      const schemaIdForColumn = columnDefinition ? getSchemaIdForEntityColumn(columnDefinition) : null;
      if (schemaIdForColumn != null) {
        sampleFilter.schemaId = schemaIdForColumn;

        if (APIUtil.apiIdToMaybeType(schemaIdForColumn) === ModelTypeConstants.TAG_SCHEMA) {
          const tagSchema = readFragmentFromCache(
            getFragmentWrapper('TagSchemaInterfaces'),
            schemaIdForColumn
          );
          // It's okay to not have the parent interfaces because most product areas don't care about them.
          // You should make sure that interfaces are queried as some point if you do care about
          // interface-schema relationships
          if (tagSchema != null) {
            sampleFilter.parentInterfaceIds = tagSchema.interfaces.map((parent) => parent.id);
          }
        }
      }

      // For each column, get all of the cells with usable values, which are in finalized rows
      const samplesInColumn = getSamplesFromSingleColumn(
        sourceTable,
        colIndex,
        TableColumnType.EXISTING_BIOENTITY,
        sampleFilter,
        columnName,
        computedTable,
        options
      );

      if (samplesInColumn != null) {
        samplesByColumn.push(samplesInColumn);
      }
    }
    // Primitive tagValue columns
    else if (
      columnDefinition?.type === TableColumnType.TAG_VALUE &&
      primitiveFieldTypeToFilter.hasOwnProperty(columnDefinition.tagSchemaFieldType)
    ) {
      const sampleFilter = primitiveFieldTypeToFilter[columnDefinition.tagSchemaFieldType]!;
      if (allowSampleType(sampleFilter.type, preFilter)) {
        const samplesInColumn = getSamplesFromSingleColumn(
          sourceTable,
          colIndex,
          TableColumnType.TAG_VALUE,
          sampleFilter,
          columnName,
          computedTable,
          options
        );

        if (samplesInColumn != null) {
          samplesByColumn.push(samplesInColumn);
        }
      }
    }
    // Custom columns
    else if (columnDefinition?.type === TableColumnType.CUSTOM) {
      assert(
        columnDefinition.customColumnConfig != null,
        'Expected custom column to have customColumnConfig defined'
      );

      const sampleFilter = dataTypeToFilter[columnDefinition.customColumnConfig.dataType];
      if (sampleFilter && allowSampleType(sampleFilter.type, preFilter)) {
        const samplesInColumn = getSamplesFromSingleColumn(
          sourceTable,
          colIndex,
          TableColumnType.CUSTOM,
          sampleFilter,
          columnName,
          computedTable,
          options
        );

        if (samplesInColumn != null) {
          samplesByColumn.push(samplesInColumn);
        }
      }
    }
    // Column of storable type can have plates or containers. We add both with a different display name.
    else {
      const plateSamples = getPlateSamples(
        preFilter,
        sourceTable,
        colIndex,
        columnName,
        computedTable,
        options
      );

      const boxSamples = getBoxSamples(preFilter, sourceTable, colIndex, columnName, computedTable, options);

      // Container columns
      const containerSamples = getContainerSamples(
        preFilter,
        columnDefinition,
        sourceTable,
        colIndex,
        columnName,
        computedTable,
        options
      );

      if (plateSamples != null) {
        samplesByColumn.push({
          ...plateSamples,
          columnDefinition: columnDefinition ?? undefined,
          displayName: columnName + (containerSamples || boxSamples ? ' (plates)' : ''),
        });
      }
      if (boxSamples != null) {
        samplesByColumn.push({
          ...boxSamples,
          columnDefinition: columnDefinition ?? undefined,
          displayName: columnName + (containerSamples || plateSamples ? ' (boxes)' : ''),
        });
      }
      if (containerSamples != null) {
        samplesByColumn.push({
          ...containerSamples,
          columnDefinition: columnDefinition!,
          displayName: columnName + (plateSamples || boxSamples ? ' (containers)' : ''),
        });
      }
    }
  }
  return samplesByColumn;
}

/**
 * Collects all of the samples sources in an entire note.
 */
export function collectSamples(
  note: Note | null,
  entryId: string,
  types: Array<CollectibleTableType>,
  options: SampleCollectionOptions = {}
): Array<SamplesSource> {
  if (note == null) {
    return [];
  }
  const sources: Array<SamplesSource> = [];

  inspectAllNoteParts(note.noteParts, (notePart) => {
    if (getNotePartType(notePart) === ModelTypeConstants.NOTE_LINKED_GROUP) {
      const group = notePart.item as UnknownNoteLinkedGroup;
      const adapter = getNoteLinkedGroupAdapters()[group.kind];
      assert(adapter != null, 'Unknown group adapter', {group});
      const groupSources = adapter.getGroupSampleSources(group, note.noteRuntimeSnapshot.evaluationSnapshot);
      if (groupSources != null) {
        sources.push(...groupSources);
      }
    }
    const tableDatas = notePartToTableDatas(notePart, types) ?? [];
    for (const tableData of tableDatas) {
      const tableName = tableData.isStructuredTable ? tableData.table.name : '';
      const computedTable = note.noteRuntimeSnapshot.evaluationSnapshot
        ? note.noteRuntimeSnapshot.evaluationSnapshot[tableName]
        : undefined;
      assert(computedTable !== undefined || tableName === '', 'Computed table was not present in the note.', {
        evaluationSnapshot: note.noteRuntimeSnapshot.evaluationSnapshot,
        tableName,
      });
      const source = getSamplesFromTableData(tableData, entryId, computedTable, {
        ...options,
        removeDuplicates: options.removeDuplicates ?? true,
        includeUnfinalizedRows: options.includeUnfinalizedRows ?? false,
      });
      if (source != null) {
        sources.push(source);
      }
    }
  });
  return sources;
}

function getSamplesFromTableData(
  tableData: CollectibleTableData,
  entryId: string,
  computedTable: Array<Array<EvaluatedCell>> | undefined,
  options: SampleCollectionOptions
): SamplesSource | null {
  let sourceTable: Table;
  let icon: BenchlingIconName;
  const omitTableNames = options.omitTableNames ?? [];

  if (tableData.legacyTableType === CollectibleLegacyTableType.LEGACY_REGISTRATION_TABLE) {
    const workflowSelector = new WorkflowSelector(getStore().getState().models.workflows);
    const stageRuns = workflowSelector.getOrderedStageRunsForEntry(entryId);
    sourceTable = convertRegistrationTableToTable(tableData.table, entryId, stageRuns);
    icon = 'register';
  } else {
    const structuredTable = tableData.table as UnknownStructuredTable;
    assert(
      isKnownStructuredTableAdapter(structuredTable.kind),
      `Table collector retrieved a table we don't know how to process`,
      {tableData}
    );
    if (!doesUserHavePermissionToViewTable(structuredTable)) {
      return null;
    }
    sourceTable = convertStructuredTableToTable(structuredTable);
    icon = getStructuredTableAdapterFromTable(structuredTable).getTableIcon(structuredTable);
  }

  if (omitTableNames.includes(sourceTable.name)) {
    return null;
  }

  const samplesByColumn = getAllSamplesByColumn(sourceTable, options.preFilter, computedTable, options);
  if (samplesByColumn.length > 0) {
    return {
      name: sourceTable.name,
      icon,
      samplesByColumn,
      table: tableData.isStructuredTable ? tableData.table : null,
    };
  }
  return null;
}

export function SamplesSourceCollector(props: SamplesSourceCollectorProps): JSX.Element {
  const noteSources = collectSamples(
    props.note,
    props.entryId,
    STRUCTURED_TABLE_TYPES_THAT_MIGHT_HAVE_SAMPLES,
    {
      omitTableNames: props.omitTableNames,
      preFilter: props.preFilter,
      removeDuplicates: false,
      includeUnfinalizedRows: true,
    }
  );

  if (!props.includeInputTables) {
    return props.children(noteSources);
  }

  return (
    <InputTablesSamplesSourceCollector entryId={props.entryId}>
      {(inputTableSources) =>
        props.children(
          // No need to force downstream re-renders if there are no input sources!
          inputTableSources.length > 0 ? [...inputTableSources, ...noteSources] : noteSources
        )
      }
    </InputTablesSamplesSourceCollector>
  );
}

/* Query for schemas and their interfaces, which is needed to match
 * schema sources to interface destinations. */
export const ALL_TAG_SCHEMA_INTERFACES_QUERY = gql`
  query AllTagSchemaInterfacesQuery @skipAudit @engComponent(name: SIGHTGLASS) {
    tagSchemas {
      id
      interfaces {
        id
        name
      }
    }
  }
`;

export default function QueryingSamplesSourceCollector(props: SamplesSourceCollectorProps): JSX.Element {
  return (
    <BenchlingQuery<AllTagSchemaInterfacesQuery>
      query={ALL_TAG_SCHEMA_INTERFACES_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {(data) => <SamplesSourceCollector {...props}>{props.children}</SamplesSourceCollector>}
    </BenchlingQuery>
  );
}
